import {
  VuexModule,
  Module,
  Mutation,
  getModule,
  Action,
} from 'vuex-module-decorators'
import store from '@/app/ui/store'
import { Pagination, Report, ReportData } from '@/domain/entities/Main'
import { Endpoints, LocalStorage, Utils } from '@/app/infrastructures/misc'
import { WHITELIST_TYPE } from '../constant'
import { container } from 'tsyringe'
import { MainPresenter } from '../presenters/MainPresenter'
import Vue from 'vue'
import io from "socket.io-client";

export interface MainState {
  openWidgetDownload: boolean
}

export interface BulkDownloadStatus {
  status: boolean
  data: BulkDownloadStatusData
  message: BulkDownloadStatusMessage
}

export interface BulkDownloadStatusMessage {
  en: string
  id: string
}

export interface BulkDownloadStatusData {
  id: number
  status: string
  created_at: string // RFC3339
  type: string
  url: string
}

@Module({ namespaced: true, store, name: 'main', dynamic: true })
class MainController extends VuexModule implements MainState {
  public openWidgetDownload = false
  public hideWidgetDownload = true
  public reportData = new ReportData()
  public loadingExport = false
  public socket: any = null;

  @Action
  getReportList() {
    this.setLoadingExport(true)
    const presenter = container.resolve(MainPresenter)
    return presenter
      .getReportList(
        new Pagination({
          perPage: this.reportData.pagination.perPage,
        })
      )
      .then(res => {
        this.setReportData(res)
      })
      .catch((error: any) => {
        Vue.notify({
          title: 'Get Report List Failed',
          text:
            error.status === 400 || error.status === 422
              ? error?.error?.message?.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => this.setLoadingExport(false))
  }
  
  @Action
  connectShipmentWebsocket() {
    if (this.socket) return;
    return new Promise((resolve) => {
      const socket = io(Endpoints.baseUrl, {
        path: "/v1/shipment/ws",
        transports: ["websocket"]
      })
      this.setSocket(socket);
      socket.on("connect", resolve)
    })
  }
  
  @Action
  subscribeBulkDownloadWS() {
    this.socket.emit("bulk_download_status", JSON.stringify({
      authorization: `Bearer ${LocalStorage.getLocalStorage("auth_token")}`
    }))
  }
  
  @Action
  async listenBulkDownloadStatusWS() {
    if (!this.reportData.data.length) await this.getReportList();
    this.socket.on("bulk_download_status", this.handleBulkDownloadStatusWSResponse);
    this.subscribeBulkDownloadWS();
  }
  
  @Action
  async handleBulkDownloadStatusWSResponse(json: string) {
    const data: BulkDownloadStatus = JSON.parse(json);
    if (data.data.status === "success") {
      this.setHideWidgetDownload(false);
      this.setOpenWidgetDownload(true);
      const linkEl = document.createElement("a");
      linkEl.setAttribute("download", "");
      linkEl.setAttribute("href", data.data.url);
      linkEl.click();
      linkEl.remove();
      const tempReportData = this.reportData
      tempReportData.data = tempReportData.data.map((report) => {
        if (report.id === data.data.id) return new Report({
          ...report,
          status: "success",
          url: data.data.url
        })
        return report
      })
      this.setReportData(new ReportData({
        ...tempReportData
      }))
    }
  }

  @Mutation
  public setOpenWidgetDownload(value: boolean) {
    this.openWidgetDownload = value
  }

  @Mutation
  public setHideWidgetDownload(value: boolean) {
    this.hideWidgetDownload = value
  }

  @Mutation
  public setReportData(res: ReportData) {
    const mappingData = res.data.map(
      (item: Report) =>
        new Report({
          ...item,
          title: `${Utils.formatDate(item.createdAt, 'HH:mm')} - Laporan ${
            WHITELIST_TYPE[item.type]
          }`,
          createdAt: Utils.formatDate(item.createdAt, 'DD MMMM YYYY'),
        })
      )
    const group: any[] = Utils.groupBy(mappingData, 'createdAt')

    this.reportData = new ReportData({
      ...res,
      pagination: new Pagination({
        ...res.pagination,
        perPage: res.data.length,
      }),
      renderedList: group,
    })
  }

  @Mutation
  public setLoadingExport(bool: boolean) {
    this.loadingExport = bool
  }
  
  @Mutation
  public setSocket(socket: any) {
    this.socket = socket;
  }
  
  @Mutation
  public prependReportList(data: Report): Promise<ReportData> {
    this.hideWidgetDownload = false;
    this.openWidgetDownload = true;
    return new Promise((resolve) => {
      const existingReport = this.reportData.data.find(report => report.id === data.id);
      if (existingReport) {
        this.reportData.data = this.reportData.data.map(report => {
          if (report.id === existingReport.id) {
            return new Report({
              ...data
            })
          }
          
          return report
        })
      } else {
        this.reportData.data.unshift(data);
      }
      
      resolve(this.reportData)
    })
  }
}

export default getModule(MainController)
